import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { analysisRegister, apiError } from "../services/AiService";
import { AnalysisRegisterItem, AnalysisStatus } from "../types/ai";
import { AnalysisStatusChip, ErrorAlert, Loading, PageShell, Pager, PAGE_SIZES, fmtDate } from "../component/aces/Shared";

const STATUSES: AnalysisStatus[] = ["QUEUED", "PROCESSING", "DRAFT_READY", "UNDER_REVIEW", "REVIEWED", "FAILED", "SUPERSEDED"];

/** Register of every QualiCheck AI analysis in the company. */
const AiAnalysesScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<AnalysisRegisterItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<AnalysisStatus | "">("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(PAGE_SIZES[0]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const timer = setTimeout(() => {
      analysisRegister({ status: status || undefined, search: search.trim() || undefined, page, size })
        .then((r) => {
          if (!active) return;
          setItems(r.items);
          setTotal(r.total);
        })
        .catch((e) => {
          if (!active) return;
          setError(apiError(e));
          setItems([]);
        });
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [status, search, page, size]);

  return (
    <PageShell title={t("aces.nav.analyses")}>
      <ErrorAlert message={error} onClose={() => setError(null)} />
      <div className="aces-filters">
        <div className="aces-field">
          <label htmlFor="analyses-search">{t("aces.reports.searchLabel")}</label>
          <input id="analyses-search" className="form-control" placeholder={t("aces.register.searchAnalyses")} value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
        </div>
        <div className="aces-field">
          <label htmlFor="analyses-status">{t("aces.analysis.status")}</label>
          <select id="analyses-status" className="form-control" value={status} onChange={(e) => { setStatus(e.target.value as AnalysisStatus | ""); setPage(0); }}>
            <option value="">{t("aces.register.allStatuses")}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{t(`aces.enum.analysis.${s}`)}</option>)}
          </select>
        </div>
      </div>
      {items === null ? <Loading /> : (
        <>
          <div className="__defaultTable aces-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("aces.register.analysis")}</th>
                  <th>{t("aces.analysis.checklist")}</th>
                  <th>{t("aces.ctx.site")}</th>
                  <th>{t("aces.analysis.status")}</th>
                  <th>{t("aces.register.findings")}</th>
                  <th>{t("aces.register.reviewer")}</th>
                  <th>{t("aces.register.date")}</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={7}>{t("aces.register.noAnalyses")}</td></tr>
                ) : items.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <button type="button" className="aces-table-link" onClick={() => navigate(`/ai/analysis/${a.id}`)}>{a.analysisRef}</button>
                      <span className="aces-muted"> v{a.version}</span>
                      {a.hasUrgent && <div><span className="aces-chip darkred">{t("aces.register.critical")}</span></div>}
                      {a.legacyAces && <div className="aces-muted">{t("aces.analysis.legacy")}</div>}
                    </td>
                    <td>{a.checklistTitle}<div className="aces-muted">{a.checklistRef}</div></td>
                    <td>{a.site || "—"}</td>
                    <td><AnalysisStatusChip value={a.status} /></td>
                    <td>{a.findingCount}</td>
                    <td>{a.reviewer || "—"}</td>
                    <td>{fmtDate(a.completedAt || a.triggeredAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} size={size} total={total} onPage={setPage} onSize={(s) => { setSize(s); setPage(0); }} />
        </>
      )}
    </PageShell>
  );
};

export default AiAnalysesScreen;
